import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core/styles";
import { useDragToAdjust } from "../hooks/useDragToAdjust";

/**
 * Per-corner border-radius editor.
 *
 * Opens as a small popover anchored to the trigger button rendered in
 * RotateComponent (right of the uniform border-radius control). It
 * supports two variants:
 *
 *   - `rect`     — 4 corners (TL/TR/BR/BL). Used by customRect /
 *                   customImage and any other rectangular shape.
 *   - `triangle` — 3 vertices (Top/BL/BR). Used by customTriangle
 *                   (a.k.a. the "Polygon" tool).
 *
 * Both variants share the same shell, the same "Link" toggle (which
 * locks all inputs together so users can quickly bump them uniformly),
 * and the same fallback semantics: any undefined per-corner field on
 * the shape's props inherits the uniform `rounded` value upstream
 * before reaching this popover. Callers are responsible for that
 * fallback — this component only reads / writes the values it's given.
 */

// ── Rect variant types ─────────────────────────────────────────────
export type CornerKey = "tl" | "tr" | "br" | "bl";
export type CornerValues = Record<CornerKey, number>;

// ── Triangle variant types ─────────────────────────────────────────
export type TriangleCornerKey = "top" | "bl" | "br";
export type TriangleCornerValues = Record<TriangleCornerKey, number>;

type CommonProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  min?: number;
  max?: number;
};

type RectVariantProps = CommonProps & {
  variant?: "rect";
  values: CornerValues;
  /** Called on every change with the new 4-value object. Caller is
   *  responsible for clamping to its own min/max range if desired. */
  onCornersChange: (next: CornerValues) => void;
};

type TriangleVariantProps = CommonProps & {
  variant: "triangle";
  values: TriangleCornerValues;
  onCornersChange: (next: TriangleCornerValues) => void;
};

type CornerRadiusPopoverProps = RectVariantProps | TriangleVariantProps;

const usePopoverStyles = makeStyles({
  paper: {
    borderRadius: 8,
    border: "1px solid #E2E2FC",
    boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.1)",
    padding: 12,
    marginTop: 6,
    background: "#fff",
  },
});

const rectLabels: Record<CornerKey, string> = {
  tl: "Top left",
  tr: "Top right",
  bl: "Bottom left",
  br: "Bottom right",
};

const triangleLabels: Record<TriangleCornerKey, string> = {
  top: "Top",
  bl: "Bottom left",
  br: "Bottom right",
};

/** Tiny inline icon hinting at which corner of a rectangle the input
 *  controls. The active corner is filled blue, the other three sit
 *  muted. Drawn as SVG so it scales with the button without any
 *  image-asset overhead. */
const RectCornerHintIcon: React.FC<{ corner: CornerKey }> = ({ corner }) => {
  const positions: Record<CornerKey, { cx: number; cy: number }> = {
    tl: { cx: 3, cy: 3 },
    tr: { cx: 13, cy: 3 },
    bl: { cx: 3, cy: 13 },
    br: { cx: 13, cy: 13 },
  };
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="#AEAFC7" strokeWidth="1" />
      {(Object.keys(positions) as CornerKey[]).map((k) => (
        <circle key={k} cx={positions[k].cx} cy={positions[k].cy} r={1.6} fill={k === corner ? "#338BFF" : "#D7D8E5"} />
      ))}
    </svg>
  );
};

/** Same idea as `RectCornerHintIcon` but for the 3-vertex triangle
 *  shape. The outline is the isoceles triangle that fits inside the
 *  16×16 box; the active vertex is filled blue. */
const TriangleCornerHintIcon: React.FC<{ corner: TriangleCornerKey }> = ({ corner }) => {
  const top = { x: 8, y: 2.5 };
  const bl = { x: 2.5, y: 13 };
  const br = { x: 13.5, y: 13 };
  const verts: Record<TriangleCornerKey, { cx: number; cy: number }> = {
    top: { cx: top.x, cy: top.y },
    bl: { cx: bl.x, cy: bl.y },
    br: { cx: br.x, cy: br.y },
  };
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d={`M ${top.x} ${top.y} L ${br.x} ${br.y} L ${bl.x} ${bl.y} Z`}
        stroke="#AEAFC7"
        strokeWidth="1"
        fill="none"
        strokeLinejoin="round"
      />
      {(Object.keys(verts) as TriangleCornerKey[]).map((k) => (
        <circle key={k} cx={verts[k].cx} cy={verts[k].cy} r={1.6} fill={k === corner ? "#338BFF" : "#D7D8E5"} />
      ))}
    </svg>
  );
};

const LinkIcon: React.FC<{ linked: boolean }> = ({ linked }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d={
        linked
          ? "M7 4.5h-1.5a3 3 0 1 0 0 6H7m2-6h1.5a3 3 0 1 1 0 6H9m-3-3h4"
          : "M6.5 4.5h-1a3 3 0 1 0 0 6h1m3-6h1a3 3 0 1 1 0 6h-1M4.5 2l7 12"
      }
      // Linked state uses the brand lavender (#E2E2FC) instead of the
      // generic blue accent so the active corner-radius lock reads as
      // part of the popover's own design system rather than a separate
      // notification colour. Stroke is darkened to #2E3860 so the icon
      // stays legible against the matching #E2E2FC background.
      stroke={linked ? "#2E3860" : "#707087"}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const inputCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  border: "1px solid #E4E4EA",
  borderRadius: 4,
  background: "#fff",
};

const numberInputStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  color: "#2E3860",
  fontFamily: "Inter, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  padding: 0,
};

export const CornerRadiusPopover: React.FC<CornerRadiusPopoverProps> = (props) => {
  const { anchorEl, onClose, min = 0, max = 100 } = props;
  const variant = props.variant ?? "rect";
  const classes = usePopoverStyles();

  // Memoize the "are all corners equal" predicate based on variant so
  // the link-toggle init / sync stays in one place regardless of how
  // many corners the variant has.
  const allEqual = useMemo<boolean>(() => {
    if (variant === "triangle") {
      const v = props.values as TriangleCornerValues;
      return v.top === v.bl && v.bl === v.br;
    }
    const v = props.values as CornerValues;
    return v.tl === v.tr && v.tr === v.br && v.br === v.bl;
  }, [variant, props.values]);

  // Local "linked" toggle. Defaults to true when the user opens the
  // popover and all corners already match — i.e. they haven't edited
  // per-corner yet, so behaving as a multi-way slider is natural.
  // If they've already broken symmetry we open with the link off so
  // we don't silently re-uniform their corners.
  const [linked, setLinked] = useState<boolean>(allEqual);

  // Sync the link toggle when the underlying values flip between
  // uniform and non-uniform from outside the popover (e.g. user edits
  // the main input while the popover is open).
  useEffect(() => {
    if (allEqual) setLinked(true);
  }, [allEqual]);

  const clamp = useCallback((n: number) => Math.max(min, Math.min(max, n)), [min, max]);

  const updateRectCorner = (corner: CornerKey, raw: string) => {
    const p = props as RectVariantProps;
    const n = clamp(Number(raw) || 0);
    if (linked) {
      p.onCornersChange({ tl: n, tr: n, br: n, bl: n });
    } else {
      p.onCornersChange({ ...p.values, [corner]: n });
    }
  };

  const updateTriangleCorner = (corner: TriangleCornerKey, raw: string) => {
    const p = props as TriangleVariantProps;
    const n = clamp(Number(raw) || 0);
    if (linked) {
      p.onCornersChange({ top: n, bl: n, br: n });
    } else {
      p.onCornersChange({ ...p.values, [corner]: n });
    }
  };

  // Order in which the inputs are laid out in the popover grid.
  // Rect: TL/TR on top row, BL/BR on bottom row — mirrors the shape.
  // Triangle: Top alone on top row, BL/BR side by side on bottom row.
  const rectOrder: CornerKey[] = ["tl", "tr", "bl", "br"];

  // Drag-to-adjust per corner. Each icon next to a per-corner input
  // doubles as a horizontal-drag handle: hold and drag right to
  // increase, left to decrease — same affordance the main corner-
  // radius input in RotateComponent already provides via
  // `useDragToAdjust`. Hooks must be called unconditionally per the
  // rules of hooks, so we set up 7 (4 rect + 3 triangle) and only
  // bind the relevant ones in JSX. Each getter reads from a ref kept
  // in sync with `props.values`, so the drag start always picks up
  // the most recent value without stale closures.
  const rectValuesRef = useRef<CornerValues>({ tl: 0, tr: 0, br: 0, bl: 0 });
  const triValuesRef = useRef<TriangleCornerValues>({ top: 0, bl: 0, br: 0 });
  if (variant === "rect") {
    rectValuesRef.current = props.values as CornerValues;
  } else {
    triValuesRef.current = props.values as TriangleCornerValues;
  }

  const tlDrag = useDragToAdjust(
    () => Math.trunc(rectValuesRef.current.tl),
    (v) => updateRectCorner("tl", String(v)),
    { min, max, step: 1 }
  );
  const trDrag = useDragToAdjust(
    () => Math.trunc(rectValuesRef.current.tr),
    (v) => updateRectCorner("tr", String(v)),
    { min, max, step: 1 }
  );
  const brDrag = useDragToAdjust(
    () => Math.trunc(rectValuesRef.current.br),
    (v) => updateRectCorner("br", String(v)),
    { min, max, step: 1 }
  );
  const blDrag = useDragToAdjust(
    () => Math.trunc(rectValuesRef.current.bl),
    (v) => updateRectCorner("bl", String(v)),
    { min, max, step: 1 }
  );
  const triTopDrag = useDragToAdjust(
    () => Math.trunc(triValuesRef.current.top),
    (v) => updateTriangleCorner("top", String(v)),
    { min, max, step: 1 }
  );
  const triBlDrag = useDragToAdjust(
    () => Math.trunc(triValuesRef.current.bl),
    (v) => updateTriangleCorner("bl", String(v)),
    { min, max, step: 1 }
  );
  const triBrDrag = useDragToAdjust(
    () => Math.trunc(triValuesRef.current.br),
    (v) => updateTriangleCorner("br", String(v)),
    { min, max, step: 1 }
  );

  // Tiny lookup tables so the JSX stays clean — pick the drag handler
  // by corner key.
  const rectDrags: Record<CornerKey, ReturnType<typeof useDragToAdjust>> = {
    tl: tlDrag,
    tr: trDrag,
    br: brDrag,
    bl: blDrag,
  };
  const triDrags: Record<TriangleCornerKey, ReturnType<typeof useDragToAdjust>> = {
    top: triTopDrag,
    bl: triBlDrag,
    br: triBrDrag,
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{ className: classes.paper }}
      // Keep clicks inside the popover from leaking to the rotate
      // component's parent — same belt-and-suspenders pattern other
      // tldraw-adjacent popovers in this folder use.
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 184 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#2E3860",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <span>Corner radius</span>
          <button
            type="button"
            onClick={() => setLinked((v) => !v)}
            title={linked ? "Unlink corners" : "Link corners"}
            aria-pressed={linked}
            data-cypress-id="corner-radius-popover-link-toggle"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 6px",
              // Active "linked" state uses the brand lavender (#E2E2FC)
              // for both border and background so the toggle matches
              // the popover's overall colour family. Text falls back to
              // #2E3860 (already used for the popover header) for
              // legibility against the lavender fill.
              border: linked ? "1px solid #E2E2FC" : "1px solid #E4E4EA",
              borderRadius: 4,
              background: linked ? "#E2E2FC" : "#fff",
              color: linked ? "#2E3860" : "#707087",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <LinkIcon linked={linked} />
            <span>{linked ? "Linked" : "Unlinked"}</span>
          </button>
        </div>

        {variant === "rect" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {rectOrder.map((corner) => {
              const v = (props as RectVariantProps).values;
              return (
                <label key={corner} title={rectLabels[corner]} style={inputCellStyle}>
                  {/* Drag-handle: holding the icon and dragging
                      horizontally adjusts this corner's value. Mirrors
                      the main BorderRadiusIcon's drag in RotateComponent. */}
                  <span
                    onMouseDown={rectDrags[corner].onMouseDown}
                    style={{ cursor: "ew-resize", display: "inline-flex" }}
                    title={`Drag to adjust ${rectLabels[corner].toLowerCase()}`}
                  >
                    <RectCornerHintIcon corner={corner} />
                  </span>
                  <input
                    type="number"
                    value={Math.trunc(v[corner])}
                    onChange={(e) => updateRectCorner(corner, e.target.value)}
                    min={min}
                    max={max}
                    aria-label={rectLabels[corner]}
                    className="no-arrows"
                    data-cypress-id={`corner-radius-popover-rect-input-${corner}`}
                    style={numberInputStyle}
                  />
                </label>
              );
            })}
          </div>
        ) : (
          // Triangle: 3 inputs in a single row — Top / BL / BR. Each
          // cell keeps the same width so the popover stays balanced
          // and we don't leave a blank slot like the previous 2-row
          // layout did.
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {(["top", "bl", "br"] as TriangleCornerKey[]).map((corner) => {
              const v = (props as TriangleVariantProps).values;
              return (
                <label key={corner} title={triangleLabels[corner]} style={inputCellStyle}>
                  {/* Same drag-handle pattern as the rect variant
                      above — see comment there for rationale. */}
                  <span
                    onMouseDown={triDrags[corner].onMouseDown}
                    style={{ cursor: "ew-resize", display: "inline-flex" }}
                    title={`Drag to adjust ${triangleLabels[corner].toLowerCase()}`}
                  >
                    <TriangleCornerHintIcon corner={corner} />
                  </span>
                  <input
                    type="number"
                    value={Math.trunc(v[corner])}
                    onChange={(e) => updateTriangleCorner(corner, e.target.value)}
                    min={min}
                    max={max}
                    aria-label={triangleLabels[corner]}
                    className="no-arrows"
                    data-cypress-id={`corner-radius-popover-triangle-input-${corner}`}
                    style={numberInputStyle}
                  />
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Popover>
  );
};

export default CornerRadiusPopover;
